import React, { useState, useEffect, useRef } from "react";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Loader2,
  CreditCard,
  Save,
  Plug,
  AlertTriangle,
  CheckCircle2,
  ShieldAlert,
  Info,
} from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { amountToCents, centsToAmount } from "../../helpers/money";

const inputClass =
  "w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all";
const labelClass =
  "text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5 block";

const BillingSettings = () => {
  const isMounted = useRef(true);
  const [allowed, setAllowed] = useState(null); // null = still checking
  const [config, setConfig] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [drafts, setDrafts] = useState({});

  useEffect(() => () => { isMounted.current = false; }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: status } = await api.get("/billing/status");
        if (!isMounted.current) return;
        if (!status.isOwner) {
          setAllowed(false);
          return;
        }
        setAllowed(true);

        const [configRes, catalogRes, gatewaysRes] = await Promise.all([
          api.get("/billing/config"),
          api.get("/billing/catalog"),
          api.get("/billing/gateways"),
        ]);
        if (!isMounted.current) return;
        setConfig(configRes.data);
        setCatalog(catalogRes.data || []);
        setGateways(gatewaysRes.data || []);
      } catch (err) {
        if (isMounted.current) setAllowed(false);
      }
    };
    load();
  }, []);

  const setField = (name, value) =>
    setConfig((prev) => ({ ...prev, [name]: value }));

  const activeEntry = catalog.find((c) => c.id === config?.activeGateway);
  const currencyMismatch =
    activeEntry && !activeEntry.currencies.includes(config?.currency);

  const handleSaveConfig = async () => {
    if (currencyMismatch) {
      toast.error(
        `${activeEntry.label} no acepta ${config.currency}. Sólo: ${activeEntry.currencies.join(", ")}.`
      );
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/billing/config", {
        enabled: config.enabled,
        currency: config.currency,
        baseAmountCents: Number(config.baseAmountCents),
        aiAmountCents: Number(config.aiAmountCents),
        aiEnabled: config.aiEnabled,
        billingDay: Number(config.billingDay),
        dueDays: Number(config.dueDays),
        graceDays: Number(config.graceDays),
        timezone: config.timezone,
        taxBasisPoints: Number(config.taxBasisPoints),
        billTemplateUsage: !!config.billTemplateUsage,
        templateMarkupBasisPoints: Number(config.templateMarkupBasisPoints || 0),
        activeGateway: config.activeGateway || null,
        enforcementEnabled: config.enforcementEnabled,
        clientName: config.clientName || null,
        clientRnc: config.clientRnc || null,
        clientEmail: config.clientEmail || null,
        ncfType: config.ncfType || null,
      });
      setConfig(data);
      toast.success("Configuración guardada");
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  const handleSaveGateway = async (entry) => {
    const gateway = gateways.find((g) => g.provider === entry.id) || {};
    const draft = drafts[entry.id] || {};
    setSaving(true);
    try {
      const { data } = await api.put(`/billing/gateways/${entry.id}`, {
        enabled: gateway.enabled,
        isSandbox: gateway.isSandbox,
        credentials: draft,
      });
      setGateways((prev) =>
        prev.map((g) => (g.provider === entry.id ? { ...g, ...data } : g))
      );
      // Clear the drafts: secrets are now stored and shown masked.
      setDrafts((prev) => ({ ...prev, [entry.id]: {} }));
      toast.success(`Credenciales de ${entry.label} guardadas`);
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  const handleTest = async (entry) => {
    setTesting(entry.id);
    try {
      const { data } = await api.post(`/billing/gateways/${entry.id}/test`);
      setTestResults((prev) => ({ ...prev, [entry.id]: data }));
    } catch (err) {
      setTestResults((prev) => ({
        ...prev,
        [entry.id]: { success: false, message: "Error al probar la conexión." },
      }));
    } finally {
      if (isMounted.current) setTesting(null);
    }
  };

  const patchGateway = (providerId, changes) =>
    setGateways((prev) =>
      prev.map((g) => (g.provider === providerId ? { ...g, ...changes } : g))
    );

  if (allowed === false) return <Redirect to="/" />;

  if (allowed === null || !config) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 max-w-3xl mx-auto space-y-4">
      <div className="mb-2">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
          Configuración de cobro
        </h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
          Sólo visible para el propietario de la plataforma
        </p>
      </div>

      {/* Plan */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Plan del cliente
          </h2>
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
            <input
              type="checkbox"
              checked={!!config.enabled}
              onChange={(e) => setField("enabled", e.target.checked)}
              className="rounded border-slate-300"
            />
            Facturación activa
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Monto mensual</label>
            <input
              type="number"
              min={0}
              step="0.01"
              value={centsToAmount(config.baseAmountCents)}
              onChange={(e) =>
                setField("baseAmountCents", amountToCents(e.target.value))
              }
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Moneda</label>
            <select
              value={config.currency}
              onChange={(e) => setField("currency", e.target.value)}
              className={inputClass}
            >
              <option value="DOP">DOP — Peso dominicano</option>
              <option value="USD">USD — Dólar</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>
              Monto por IA
              <span className="ml-2 font-normal normal-case">
                <input
                  type="checkbox"
                  checked={!!config.aiEnabled}
                  onChange={(e) => setField("aiEnabled", e.target.checked)}
                  className="rounded border-slate-300 mr-1"
                />
                cobrar
              </span>
            </label>
            <input
              type="number"
              min={0}
              step="0.01"
              disabled={!config.aiEnabled}
              value={centsToAmount(config.aiAmountCents)}
              onChange={(e) =>
                setField("aiAmountCents", amountToCents(e.target.value))
              }
              className={`${inputClass} disabled:opacity-50`}
            />
          </div>
          <div>
            <label className={labelClass}>ITBIS (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={(config.taxBasisPoints / 100).toFixed(2)}
              onChange={(e) =>
                setField(
                  "taxBasisPoints",
                  Math.round((Number(e.target.value) || 0) * 100)
                )
              }
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Día de pago</label>
            <select
              value={config.billingDay}
              onChange={(e) => setField("billingDay", e.target.value)}
              className={inputClass}
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
            <p className="text-[11px] text-slate-400 mt-1">
              Sólo del 1 al 28: los días 29-31 no existen en todos los meses.
            </p>
          </div>
          <div>
            <label className={labelClass}>Zona horaria</label>
            <input
              type="text"
              value={config.timezone || ""}
              onChange={(e) => setField("timezone", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Días para vencer</label>
            <input
              type="number"
              min={0}
              max={60}
              value={config.dueDays}
              onChange={(e) => setField("dueDays", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Días de gracia antes de suspender</label>
            <input
              type="number"
              min={0}
              max={90}
              value={config.graceDays}
              onChange={(e) => setField("graceDays", e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
          <input
            type="checkbox"
            checked={!!config.enforcementEnabled}
            onChange={(e) => setField("enforcementEnabled", e.target.checked)}
            className="rounded border-slate-300"
          />
          Suspender el acceso tras los días de gracia
        </label>

        {/* Traslado del gasto de plantillas. Opt-in porque cambia lo que paga
            el cliente, y con avisos porque el importe pasa a ser variable. */}
        <div className="pt-3 border-t border-slate-100 dark:border-slate-800 space-y-3">
          <label className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
            <input
              type="checkbox"
              checked={!!config.billTemplateUsage}
              onChange={(e) => setField("billTemplateUsage", e.target.checked)}
              className="mt-0.5 rounded border-slate-300"
            />
            <span>
              Cobrarle al cliente el gasto de plantillas de WhatsApp
              <span className="block text-[11px] text-slate-400 mt-0.5">
                Se agrega como una línea más en la factura mensual, con el
                consumo del período ya cerrado (la factura se emite al inicio
                de su período, cuando ese consumo todavía no existe).
              </span>
            </span>
          </label>

          {config.billTemplateUsage && (
            <>
              <div className="max-w-xs">
                <label className={labelClass}>Recargo sobre el costo (%)</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={((config.templateMarkupBasisPoints || 0) / 100).toFixed(2)}
                  onChange={(e) =>
                    setField(
                      "templateMarkupBasisPoints",
                      Math.round((Number(e.target.value) || 0) * 100)
                    )
                  }
                  className={inputClass}
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  0 = se traslada al costo, sin ganar ni perder.
                </p>
              </div>

              {!config.metaFxRateMilli && (
                <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800 dark:text-amber-300">
                    Falta el tipo de cambio en <strong>Costos</strong>. Si las
                    tarifas de Meta están en otra moneda que el cobro, el
                    consumo <strong>no se va a facturar</strong> — preferimos no
                    cobrar antes que cobrar un importe equivocado.
                  </p>
                </div>
              )}

              <div className="flex items-start gap-2 p-3 rounded-lg border border-indigo-200 dark:border-indigo-900 bg-indigo-50 dark:bg-indigo-950/30">
                <Info className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-indigo-800 dark:text-indigo-300">
                  El total pasa a variar cada mes. Las suscripciones de pasarelas
                  que cobran un importe fijo (como Stripe) dejarán de cubrir la
                  factura completa: se marcará como importe desfasado y habrá que
                  recrearla, o usar Azul (que cobra el monto real de cada
                  factura).
                </p>
              </div>
            </>
          )}
        </div>
        {config.suspendedAt && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900">
            <ShieldAlert className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-700 dark:text-red-300">
              Esta instalación está suspendida desde el{" "}
              {new Date(config.suspendedAt).toLocaleString("es-DO")}. Se levanta sola
              al registrarse un pago, o desactivando la casilla de arriba.
            </p>
          </div>
        )}
      </div>

      {/* Datos del cliente */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 space-y-4">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Datos del cliente para la factura
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { key: "clientName", label: "Razón social" },
            { key: "clientRnc", label: "RNC" },
            { key: "clientEmail", label: "Correo" },
            { key: "ncfType", label: "Tipo de NCF" },
          ].map((field) => (
            <div key={field.key}>
              <label className={labelClass}>{field.label}</label>
              <input
                type="text"
                value={config[field.key] || ""}
                onChange={(e) => setField(field.key, e.target.value)}
                className={inputClass}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Pasarelas */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 space-y-4">
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-indigo-600" />
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Pasarelas de pago
          </h2>
        </div>

        <div>
          <label className={labelClass}>Pasarela activa</label>
          <select
            value={config.activeGateway || ""}
            onChange={(e) => setField("activeGateway", e.target.value || null)}
            className={inputClass}
          >
            <option value="">Sin pasarela</option>
            {catalog.map((entry) => (
              <option key={entry.id} value={entry.id}>{entry.label}</option>
            ))}
          </select>
          {currencyMismatch && (
            <p className="text-xs text-red-500 mt-1.5">
              {activeEntry.label} no acepta {config.currency}. Monedas admitidas:{" "}
              {activeEntry.currencies.join(", ")}.
            </p>
          )}
        </div>

        {catalog.map((entry) => {
          const gateway =
            gateways.find((g) => g.provider === entry.id) || {
              enabled: false,
              isSandbox: true,
              credentials: {},
            };
          const draft = drafts[entry.id] || {};
          const result = testResults[entry.id];

          return (
            <div
              key={entry.id}
              className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-3"
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {entry.label}
                  </p>
                  <p className="text-[11px] text-slate-400">
                    {entry.currencies.join(", ")}
                    {entry.supportsAutoRecurring
                      ? " · cobro automático"
                      : " · cobro manual"}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                    <input
                      type="checkbox"
                      checked={!!gateway.isSandbox}
                      onChange={(e) =>
                        patchGateway(entry.id, { isSandbox: e.target.checked })
                      }
                      className="rounded border-slate-300"
                    />
                    Sandbox
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                    <input
                      type="checkbox"
                      checked={!!gateway.enabled}
                      onChange={(e) =>
                        patchGateway(entry.id, { enabled: e.target.checked })
                      }
                      className="rounded border-slate-300"
                    />
                    Activa
                  </label>
                </div>
              </div>

              {entry.warning && (
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-[11px] text-amber-700 dark:text-amber-300">
                    {entry.warning}
                  </p>
                </div>
              )}
              {entry.hint && (
                <p className="text-[11px] text-slate-400">{entry.hint}</p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {entry.credentialFields.map((field) => {
                  const stored = gateway.credentials?.[field.key];
                  const isConfigured =
                    field.secret && stored && stored.configured;
                  // Set in docker-compose: editing it here would be ignored,
                  // because the environment wins in resolvePaymentProvider.
                  const fromEnv = (gateway.envManagedKeys || []).includes(
                    field.key
                  );
                  const value = field.secret
                    ? draft[field.key] ?? ""
                    : draft[field.key] ?? (typeof stored === "string" ? stored : "");

                  const InputTag = field.multiline ? "textarea" : "input";

                  return (
                    <div
                      key={field.key}
                      className={field.multiline ? "sm:col-span-2" : ""}
                    >
                      <label className={labelClass}>
                        {field.label}
                        {fromEnv ? (
                          <span className="ml-1.5 text-indigo-600 font-normal">
                            desde {gateway.envNames?.[field.key]}
                          </span>
                        ) : (
                          isConfigured && (
                            <span className="ml-1.5 text-emerald-600 font-normal">
                              guardada ({stored.masked})
                            </span>
                          )
                        )}
                      </label>
                      <InputTag
                        type={field.secret ? "password" : "text"}
                        rows={field.multiline ? 4 : undefined}
                        disabled={fromEnv}
                        placeholder={
                          fromEnv
                            ? "Definida por variable de entorno"
                            : isConfigured
                            ? "Dejar en blanco para conservar la actual"
                            : field.placeholder || ""
                        }
                        value={fromEnv ? "" : value}
                        onChange={(e) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [entry.id]: {
                              ...(prev[entry.id] || {}),
                              [field.key]: e.target.value,
                            },
                          }))
                        }
                        className={`${inputClass} disabled:opacity-60 disabled:cursor-not-allowed ${field.multiline ? "font-mono text-xs resize-none" : ""}`}
                      />
                    </div>
                  );
                })}

                {(gateway.envManagedKeys || []).length > 0 && (
                  <p className="sm:col-span-2 text-[11px] text-indigo-600 dark:text-indigo-400">
                    Algunas credenciales vienen del entorno del servidor y tienen
                    prioridad sobre lo que se guarde aquí. Es la forma recomendada:
                    el secreto no queda copiado en la base de datos de cada cliente.
                  </p>
                )}
              </div>

              {result && (
                <div
                  className={`flex items-start gap-2 text-xs ${
                    result.success ? "text-emerald-600" : "text-red-500"
                  }`}
                >
                  {result.success ? (
                    <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  )}
                  {result.message}
                </div>
              )}

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleSaveGateway(entry)}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  <Save className="w-3.5 h-3.5" />
                  Guardar
                </button>
                <button
                  onClick={() => handleTest(entry)}
                  disabled={testing === entry.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 transition-colors"
                >
                  {testing === entry.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Plug className="w-3.5 h-3.5" />
                  )}
                  Probar conexión
                </button>
                <a
                  href={entry.docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[11px] text-indigo-600 hover:underline ml-auto"
                >
                  Documentación
                </a>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSaveConfig}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Guardar configuración
        </button>
      </div>
    </div>
  );
};

export default BillingSettings;
